import React, { useCallback, useMemo, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Scrollbar } from "swiper";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import "swiper/css";
import "swiper/css/scrollbar";

import Style from "./PromoBanner.module.scss";
import BannerSlide, { isVideoSlide } from "./BannerSlide";
import useMediaQuery from "./useMediaQuery";
import { useBanner } from "../../logic/useBanner";

const DEFAULT_DELAY = 5000;
const TRANSITION_SPEED = 500;
const DESKTOP_QUERY = "(min-width: 768px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

const hasMedia = (slide) =>
  slide && (slide.media_desktop_url || slide.media_mobile_url);

/**
 * Backend-driven promotional banner carousel.
 *
 * Slides can be images, GIFs or videos. Renders nothing at all when the API returns no
 * live banner, so the page is unchanged until a campaign is switched on. A single slide
 * renders as a plain static banner with no carousel controls.
 *
 * API contract and artwork sizes: docs/promo-banner-spec.md
 */
const PromoBanner = () => {
  const { banner } = useBanner();
  const { t } = useTranslation(["common"]);
  const { lang } = useParams();

  const [swiper, setSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [brokenIds, setBrokenIds] = useState([]);

  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const reducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);

  const slides = useMemo(() => {
    if (!banner || Number(banner.status) !== 1) return [];
    if (!Array.isArray(banner.slides)) return [];
    return banner.slides
      .filter(hasMedia)
      .filter((slide, i) => !brokenIds.includes(slide.id ?? i));
  }, [banner, brokenIds]);

  // A slide whose media 404s is dropped rather than left as an empty box.
  const handleError = useCallback((id) => {
    setBrokenIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  // Autoplay advances image slides on a timer, but a video slide holds the carousel
  // until the clip finishes - otherwise a clip longer than the delay gets cut off.
  const syncAutoplay = useCallback((instance) => {
    setActiveIndex(instance.realIndex);
    if (reducedMotion || !instance.autoplay) return;
    if (isVideoSlide(slides[instance.realIndex])) {
      instance.autoplay.stop();
    } else if (!instance.autoplay.running) {
      instance.autoplay.start();
    }
  }, [slides, reducedMotion]);

  // onSlideChange does not fire for the slide Swiper starts on, so run the same check
  // on init - otherwise a video in position 1 gets cut off by the autoplay timer.
  const handleSwiper = useCallback((instance) => {
    setSwiper(instance);
    syncAutoplay(instance);
  }, [syncAutoplay]);

  const handleVideoEnded = useCallback(() => {
    if (!swiper || swiper.destroyed) return;
    // Advancing fires onSlideChange, which restarts autoplay only if the next slide is
    // not itself a video. Restarting it here too would break that.
    swiper.slideNext(TRANSITION_SPEED);
  }, [swiper]);

  if (!slides.length) return null;

  const renderSlide = (slide, i) => (
    <BannerSlide
      slide={slide}
      isActive={i === activeIndex}
      isDesktop={isDesktop}
      reducedMotion={reducedMotion}
      isOnlySlide={slides.length === 1}
      isFirst={i === 0}
      onEnded={handleVideoEnded}
      onError={() => handleError(slide.id ?? i)}
    />
  );

  // One slide needs no carousel machinery - render it exactly as a static banner.
  if (slides.length === 1) {
    return (
      <div className={Style.promo_banner}>
        <div className={Style.frame}>{renderSlide(slides[0], 0)}</div>
      </div>
    );
  }

  const autoplay = reducedMotion
    ? false
    : {
        delay: Number(banner.autoplay_delay) || DEFAULT_DELAY,
        pauseOnMouseEnter: true,
        disableOnInteraction: false,
      };

  return (
    <div className={Style.promo_banner}>
      <Swiper
        className={Style.swiper}
        modules={[Autoplay, Scrollbar]}
        dir={lang === "ar" ? "rtl" : "ltr"}
        slidesPerView={1}
        speed={TRANSITION_SPEED}
        rewind
        allowTouchMove
        edgeSwipeThreshold={5}
        autoplay={autoplay}
        // No `el` selector: swiper/react creates the scrollbar and binds it by ref, so
        // it cannot collide with the global ".swiper-scrollbar" the gift-card carousel
        // below this component binds to.
        scrollbar={{ draggable: true }}
        onSwiper={handleSwiper}
        onSlideChange={syncAutoplay}
      >
        {slides.map((slide, i) => (
          <SwiperSlide key={slide.id ?? i} className={Style.frame}>
            {renderSlide(slide, i)}
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom arrows rather than Swiper's Navigation module: importing
          "swiper/css/navigation" would also un-hide the gift-card carousel's arrows,
          which are deliberately invisible today. */}
      <button
        type="button"
        className={`${Style.nav} ${Style.nav_prev}`}
        aria-label={t("Previous banner")}
        onClick={() => swiper && swiper.slidePrev(TRANSITION_SPEED)}
      >
        <span className={Style.nav_icon} aria-hidden="true" />
      </button>
      <button
        type="button"
        className={`${Style.nav} ${Style.nav_next}`}
        aria-label={t("Next banner")}
        onClick={() => swiper && swiper.slideNext(TRANSITION_SPEED)}
      >
        <span className={Style.nav_icon} aria-hidden="true" />
      </button>
    </div>
  );
};

export default PromoBanner;
